import React, { useState } from 'react';
import styled from 'styled-components';
import breakpoints from '@repo/ui-components/breakpoints';
import styles from '@utils/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import LazyLoad from 'react-lazyload';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const NewsDescription = styled.p`
  font-size: ${styles.fontSize.sm};
  line-height: 22px;
  color: ${styles.colors.gray[500]};
  margin-bottom: 15px;
  font-weight: ${styles.fontWeight.light};
`;

const SuccessSignUp = styled.p`
  color: ${styles.colors.gray[500]};
  font-size: ${styles.fontSize.sm};
  font-weight: ${styles.fontWeight.medium};
`;

const ErrorMessage = styled.p`
  color: ${styles.colors.red[200]};
  font-size: ${styles.fontSize.sm};
`;

const FormContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  border-bottom: 2px solid ${styles.colors.blue[100]};
  width: 100%;

  .grecaptcha-badge {
    display: none;
    visibility: hidden;
  }

  @media (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg}) {
    width: 50%;
  }
`;

const StyledInput = styled.input`
  height: 34px;
  font-size: ${styles.fontSize.sm};
  padding: 0.5rem;
  border-color: transparent;
  max-width: calc(100% - 85px);
  min-width: calc(100% - 85px);
  font-weight: ${styles.fontWeight.light};

  &:focus {
    outline: none;
  }
`;

const StyledButton = styled.button`
  text-align: right;
  margin: 0;
  color: ${styles.colors.blue[100]};
  padding: 4px;
  font-size: ${styles.fontSize.sm};
  font-weight: ${styles.fontWeight.medium};
  height: auto;
  background: white;
  min-width: 85px;
  max-width: 85px;
  display: block;
  width: 100%;
  border-radius: 0;
  border: 1px solid transparent;
  line-height: normal;
  order: 1;
  transition: all 0.15s ease;
  cursor: pointer;

  &:hover {
    color: ${styles.colors.orange[500]};
  }
`;

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .email('Invalid email address')
    .required('Invalid email address')
    .label('Email Address'),
});

type NewsletterFormInputs = {
  email: string;
};

const Newsletter = () => {
  const [formVisible, setFormVisible] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewsletterFormInputs>({
    resolver: yupResolver(validationSchema),
  });

  const recaptchaRef = React.useRef<ReCAPTCHA | null>(null);

  const onSubmit = async (formData, e) => {
    try {
      e.preventDefault();
      const gRecaptchaResponse = await recaptchaRef.current.executeAsync();
      if (!gRecaptchaResponse) return;

      const options = {
        method: 'POST',
        headers: { revision: '2024-05-15', 'content-type': 'application/json' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'footer newsletter signup form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: formData.email,
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: 'KkbGWg' } } },
          },
        }),
      };

      await fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=gT3Eag',
        options
      );
      setFormVisible(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <NewsDescription>
        Stay up-to-date with our latest deals and product news.
      </NewsDescription>
      {formVisible && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormContainer>
            <StyledInput
              type="email"
              placeholder="Email Address"
              {...register('email')}
            />
            {process.env.GATSBY_KURGO_RECAPTCHA_KEY_INVISIBLE && (
              <LazyLoad height="100%">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.GATSBY_KURGO_RECAPTCHA_KEY_INVISIBLE}
                  badge="inline"
                  size="invisible"
                />
              </LazyLoad>
            )}
            <StyledButton type="submit">Submit &gt;</StyledButton>
          </FormContainer>
          <ErrorMessage>{errors.email?.message}</ErrorMessage>
        </form>
      )}
      {!formVisible && (
        <SuccessSignUp>
          Thank you for joining our Kurgo mailing list!
        </SuccessSignUp>
      )}
    </>
  );
};

export default Newsletter;
